import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

class BackInStockComponent extends Component {
  #abortController = new AbortController();
  #state = {
    currentVariantId: "",
    currentVariantAvailable: true,
    comingSoon: false,
    isWaitlistProduct: false,
    productId: "",
    productTitle: "",
    isCookbook: false,
    unavailableVariants: [],
    messages: {},
    buttonLabels: {},
  };

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    this.#hydrateState();
    this.#cacheElements();
    this.#bindEvents();
    this.#render();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    super.disconnectedCallback();
  }

  #cacheElements() {
    this.productForm = this.closest("product-form-component");
    this.scope = this.closest(".shopify-section, dialog, product-card");
    this.addToCartContainer = this.productForm?.querySelector('add-to-cart-component[ref="addToCartButtonContainer"]');
    this.acceleratedCheckout = this.productForm?.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    this.triggerButton = this.querySelector("[data-back-in-stock-trigger]");
    this.triggerText = this.querySelector("[data-back-in-stock-button-text]");
    // Keep the trigger paired with the dialog rendered in this product card.
    // This avoids opening the first matching dialog when a product is rendered
    // more than once on a page.
    this.dialogComponent = this.querySelector("dialog-component");
    this.closeButton = this.querySelector("[data-back-in-stock-close]");
    this.productTitle = this.querySelector("[data-back-in-stock-product-title]");
    this.modalHeading = this.querySelector("[data-back-in-stock-heading]");
    this.modalDescription = this.querySelector("[data-back-in-stock-description]");
    this.form = this.querySelector("[data-back-in-stock-form]");
    this.variantField = this.querySelector("[data-back-in-stock-variant-field]");
    this.variantSelect = this.querySelector("[data-back-in-stock-variant-select]");
    this.variantInput = this.querySelector("[data-back-in-stock-variant-input]");
    this.emailInput = this.querySelector("[data-back-in-stock-email]");
    this.submitButton = this.querySelector("[data-back-in-stock-submit]");
    this.submitText = this.querySelector("[data-back-in-stock-submit-text]");
    this.spinner = this.querySelector("[data-back-in-stock-spinner]");
    this.message = this.querySelector("[data-back-in-stock-message]");
  }

  #hydrateState() {
    this.#state.productId = String(this.dataset.productId || "");
    this.#state.productTitle = this.dataset.productTitle || "";
    this.#state.currentVariantId = String(this.dataset.currentVariantId || "");
    this.#state.currentVariantAvailable = this.dataset.currentVariantAvailable === "true";
    this.#state.comingSoon = this.dataset.comingSoon === "true";
    this.#state.isWaitlistProduct = this.dataset.isWaitlistProduct === "true";
    this.#state.isCookbook = this.dataset.isCookbook === "true";
    this.#state.unavailableVariants = this.#parseJson(this.dataset.unavailableVariants, []);
    this.#state.messages = this.#parseJson(this.dataset.messages, {});
    this.#state.buttonLabels = {
      notify: this.dataset.buttonNotifyLabel || "Notify me",
      soldOut: this.dataset.buttonSoldOutLabel || "Sold out",
      waitlist: this.dataset.buttonWaitlistLabel || "Get On The Waitlist",
    };

    this.#state.unavailableVariants = this.#state.unavailableVariants.map((variant) => ({
      id: String(variant.id ?? ""),
      title: variant.title ?? "",
    }));
  }

  #parseJson(value, fallback) {
    if (!value) return fallback;

    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }

  #bindEvents() {
    const { signal } = this.#abortController;

    this.scope?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    this.scope?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    this.triggerButton?.addEventListener("click", this.#onTriggerClick, { signal });
    this.closeButton?.addEventListener("click", this.#onCloseClick, { signal });
    this.variantSelect?.addEventListener("change", this.#onVariantSelectChange, { signal });
    this.submitButton?.addEventListener("click", this.#onSubmit, { signal });
    this.emailInput?.addEventListener("keydown", this.#onEmailKeydown, { signal });
  }

  #onVariantSelected = () => {
    if (!this.triggerButton || this.triggerButton.hidden) return;
    this.triggerButton.disabled = true;
    this.triggerButton.setAttribute("aria-disabled", "true");
  };

  #onVariantUpdate = (event) => {
    const eventProductId = String(event.detail?.data?.productId || "");
    const newProductId = String(event.detail?.data?.newProduct?.id || "");

    if (eventProductId !== this.#state.productId && newProductId !== this.#state.productId) return;

    const variant = event.detail?.resource;
    this.#state.currentVariantId = variant?.id ? String(variant.id) : "";
    this.#state.currentVariantAvailable = variant?.available ?? false;

    this.#render();
  };

  #render() {
    const canNotify = this.#canNotify();
    const showBackInStock = this.#shouldShowBackInStock();

    if (this.addToCartContainer) {
      this.addToCartContainer.hidden = showBackInStock;
    }

    if (this.acceleratedCheckout) {
      if (showBackInStock) {
        this.acceleratedCheckout.setAttribute("hidden", "true");
      } else {
        this.acceleratedCheckout.removeAttribute("hidden");
      }
    }

    if (this.triggerButton) {
      this.triggerButton.hidden = !showBackInStock;
      this.triggerButton.disabled = !canNotify;
      this.triggerButton.setAttribute("aria-disabled", canNotify ? "false" : "true");
    }

    if (this.triggerText) {
      this.triggerText.textContent = this.#getButtonLabel(canNotify);
    }

    if (this.productTitle) {
      this.productTitle.textContent =
        this.#state.productTitle || this.#state.messages.dialogFallbackTitle || "Back in stock notification";
    }

    if (this.modalHeading) {
      this.modalHeading.textContent = this.#state.isCookbook
        ? this.#state.messages.cookbookHeading
        : this.#state.messages.notifyHeading;
    }

    if (this.modalDescription) {
      this.modalDescription.textContent = this.#state.isCookbook
        ? this.#state.messages.cookbookDescription
        : this.#state.messages.notifyDescription;
    }

    if (this.submitText) {
      this.submitText.textContent = this.#state.isCookbook
        ? this.#state.messages.cookbookSubmit
        : this.#state.messages.notifySubmit;
    }

    this.#syncVariantField();
    this.scope?.querySelector("sticky-add-to-cart")?.syncNotifyButton?.();
  }

  #shouldShowBackInStock() {
    return !this.#state.currentVariantAvailable;
  }

  #canNotify() {
    return this.#state.comingSoon && this.#selectedVariantIsUnavailable();
  }

  #selectedVariantIsUnavailable() {
    return this.#state.unavailableVariants.some((variant) => variant.id === this.#state.currentVariantId);
  }

  #getButtonLabel(canNotify) {
    if (!canNotify) return this.#state.buttonLabels.soldOut;
    if (this.#state.isWaitlistProduct) return this.#state.buttonLabels.waitlist;
    return this.#state.buttonLabels.notify;
  }

  #syncVariantField() {
    if (!this.variantField || !this.variantInput) return;

    const variants = this.#state.unavailableVariants;
    const selectedId = variants.some((variant) => variant.id === this.#state.currentVariantId)
      ? this.#state.currentVariantId
      : "";

    this.variantInput.value = selectedId;

    if (!this.variantSelect) return;

    if (variants.length <= 1) {
      this.variantField.classList.add("hidden");
      this.variantSelect.innerHTML = "";
      return;
    }

    this.variantField.classList.remove("hidden");
    this.variantSelect.innerHTML = "";

    for (const variant of variants) {
      const option = document.createElement("option");
      option.value = variant.id;
      option.textContent = variant.title;
      option.selected = variant.id === selectedId;
      this.variantSelect.appendChild(option);
    }

    if (selectedId) {
      this.variantSelect.value = selectedId;
    }
  }

  #onTriggerClick = () => {
    if (!this.#canNotify()) return;
    if (!this.dialogComponent || typeof this.dialogComponent.showDialog !== "function") return;

    this.#resetFormState();
    this.#syncVariantField();
    this.dialogComponent.showDialog();
  };

  #onCloseClick = (event) => {
    event.preventDefault();
    this.dialogComponent?.closeDialog();
  };

  #onVariantSelectChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement) || !this.variantInput) return;
    this.variantInput.value = String(event.target.value || "");
  };

  #onEmailKeydown = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    this.#onSubmit();
  };

  #resetFormState() {
    this.message?.replaceChildren();
    this.form?.removeAttribute("aria-busy");
    this.submitButton?.classList.remove("btn--loading");
    this.submitButton?.removeAttribute("aria-busy");
    this.submitButton?.removeAttribute("disabled");
    this.spinner?.classList.add("hidden");
    if (this.emailInput) {
      this.emailInput.setCustomValidity("");
    }
  }

  #setLoading(isLoading) {
    if (!this.submitButton || !this.spinner || !this.form) return;

    this.submitButton.toggleAttribute("disabled", isLoading);
    this.submitButton.classList.toggle("btn--loading", isLoading);
    this.submitButton.setAttribute("aria-busy", isLoading ? "true" : "false");
    this.form.setAttribute("aria-busy", isLoading ? "true" : "false");
    this.spinner.classList.toggle("hidden", !isLoading);
  }

  #renderMessage(type, text) {
    if (!this.message) return;

    this.message.replaceChildren();

    if (!text) return;

    const message = document.createElement("p");
    message.className = type === "success" ? "alert alert--success" : "alert alert--error";
    message.textContent = text;
    this.message.appendChild(message);
  }

  #getSelectedVariantIdForSubmit() {
    return String(this.variantInput?.value || "");
  }

  #onSubmit = async () => {
    if (!this.emailInput || !this.submitButton) return;
    if (this.submitButton.disabled) return;

    const email = this.emailInput.value.trim();
    const variantId = this.#getSelectedVariantIdForSubmit();
    const productId = this.#state.productId;

    this.emailInput.setCustomValidity("");

    if (!email || !this.emailInput.checkValidity() || !variantId || !productId) {
      this.emailInput.reportValidity();
      this.#renderMessage("error", this.#state.messages.validationError);
      return;
    }

    if (!window.BIS || typeof window.BIS.create !== "function") {
      this.#renderMessage("error", this.#state.messages.missingBis);
      return;
    }

    this.#setLoading(true);
    this.#renderMessage("info", "");

    try {
      const response = await window.BIS.create(email, variantId, productId);

      if (response?.status === "OK") {
        this.#renderMessage("success", response.message || this.#state.messages.notifySubmit);
      } else if (response?.status === "Error") {
        const errorMessage =
          Array.isArray(response?.errors?.base) && response.errors.base.length > 0
            ? response.errors.base.join(" ")
            : this.#state.messages.fallbackError;
        this.#renderMessage("error", errorMessage);
      } else {
        this.#renderMessage("error", this.#state.messages.fallbackError);
      }
    } catch {
      this.#renderMessage("error", this.#state.messages.fallbackError);
    } finally {
      this.#setLoading(false);
    }
  };
}

if (!customElements.get("back-in-stock-component")) {
  customElements.define("back-in-stock-component", BackInStockComponent);
}
